<template>
  <!--
    What a reader sees when the embed read fails: one screen per kind of failure
    (`embedFailure.ts`), in the browser's language, in the embed page's own dark
    frame. `backend/crates/bin/api/templates/embed_refusal.html` words a visitor's
    refusals before the player exists, with these French words and in French only;
    keep the two in step. The API's English sentence is never shown.
    RGAA: announced to assistive tech (role=alert), icon hidden from the a11y tree.
  -->
  <div class="player-refusal" role="alert">
    <v-icon size="40" :icon="icon" class="player-refusal__icon" aria-hidden="true" />
    <p class="player-refusal__title">{{ copy.title }}</p>
    <p class="player-refusal__text">{{ copy.text }}</p>
    <v-btn v-if="retryable" variant="tonal" color="primary" size="small" @click="emit('retry')">
      {{ language.retry }}
    </v-btn>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import {
    mdiAccountClockOutline,
    mdiAlertCircleOutline,
    mdiCancel,
    mdiEyeOffOutline,
    mdiLockOutline,
    mdiTimerSandEmpty,
  } from '@mdi/js'
  import { currentLocale } from '../locale'
  import { isRetryable, type EmbedFailure } from '../embedFailure'

  // `canRetry` is false where the host has nothing to reload (an embed handed in whole).
  const props = withDefaults(defineProps<{ failure: EmbedFailure; canRetry?: boolean }>(), {
    canRetry: true,
  })
  const emit = defineEmits<{ retry: [] }>()

  const COPY = {
    fr: {
      retry: 'Réessayer',
      unavailable: {
        title: 'Ce contenu n’est pas disponible',
        text: 'Le lien a peut-être expiré ou été désactivé.',
      },
      restricted: {
        title: 'Accès réservé',
        text: 'Ce lien ne donne pas accès à ce contenu. Demandez un nouveau lien à la personne qui l’a partagé.',
      },
      origin: {
        title: 'Lecture non autorisée ici',
        text: 'Ce contenu ne peut être lu que sur les sites autorisés par son propriétaire.',
      },
      throttled: {
        title: 'Trop de tentatives',
        text: 'Réessayez dans quelques secondes.',
      },
      outage: {
        title: 'Lecteur momentanément indisponible',
        text: 'Réessayez dans quelques instants.',
      },
      missing: {
        title: 'Ce média est introuvable',
        text: 'Il a peut-être été supprimé ou placé dans la corbeille.',
      },
      denied: {
        title: 'Accès refusé',
        text: 'Vous n’avez pas les droits nécessaires pour lire ce média.',
      },
      signedOut: {
        title: 'Session expirée',
        text: 'Reconnectez-vous pour lire ce média.',
      },
    },
    en: {
      retry: 'Try again',
      unavailable: {
        title: 'This content is not available',
        text: 'The link may have expired or been switched off.',
      },
      restricted: {
        title: 'Restricted access',
        text: 'This link does not give access to this content. Ask the person who shared it for a new one.',
      },
      origin: {
        title: 'Playback not allowed here',
        text: 'This content only plays on the sites its owner allowed.',
      },
      throttled: {
        title: 'Too many attempts',
        text: 'Try again in a few seconds.',
      },
      outage: {
        title: 'The player is temporarily unavailable',
        text: 'Try again in a moment.',
      },
      missing: {
        title: 'This media cannot be found',
        text: 'It may have been deleted or moved to the trash.',
      },
      denied: {
        title: 'Access denied',
        text: 'You do not have the rights needed to play this media.',
      },
      signedOut: {
        title: 'Session expired',
        text: 'Sign in again to play this media.',
      },
    },
  } as const

  const ICONS: Record<EmbedFailure, string> = {
    unavailable: mdiEyeOffOutline,
    restricted: mdiLockOutline,
    origin: mdiCancel,
    throttled: mdiTimerSandEmpty,
    outage: mdiAlertCircleOutline,
    missing: mdiEyeOffOutline,
    denied: mdiLockOutline,
    signedOut: mdiAccountClockOutline,
  }

  const language = computed(() => (currentLocale.value === 'fr' ? COPY.fr : COPY.en))
  const copy = computed(() => language.value[props.failure])
  const icon = computed(() => ICONS[props.failure])
  const retryable = computed(() => props.canRetry && isRetryable(props.failure))
</script>

<style scoped>
  .player-refusal {
    width: 100%;
    height: 100%;
    min-height: 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 24px;
    text-align: center;
    background: #121212;
    color: #ffffff;
  }

  .player-refusal__icon {
    color: #b4b9c2;
  }

  .player-refusal__title {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.4;
    max-width: 36ch;
  }

  .player-refusal__text {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: #b4b9c2;
    max-width: 44ch;
  }
</style>
